import { type PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import '@/shared/consts/events.quiz-school-package';
import { useImpressionRef } from '@zep/logger/react';
import { trackEvent } from '@zep/logger/tracking';
import { useUser } from '@/lib/auth';
import { useIsLoggerUserInfoSet } from '@/lib/auth/UserContext';
import { type SchoolCtaEvent } from '@/shared/consts/events.quiz-school-package';
import { useCountryCode } from '@/shared/hooks/useCountryCode';
import { getSchoolCountryCode, getSchoolFunnelContext } from '@/shared/utils/schoolPackageTracking';
import { createTrackingId } from '@/utils/analytics/trackingId';
export const SchoolPackageCta = ({
  ctaType,
  available,
  ctaLocation,
  planType = 'PRO',
  className = 'w-full',
  onClick,
  children
}: PropsWithChildren<{
  ctaType: SchoolCtaEvent['cta_type'];
  ctaLocation?: SchoolCtaEvent['cta_location'];
  planType?: 'BASIC' | 'PRO';
  className?: string;
  available: boolean;
  onClick: (context: SchoolCtaEvent) => void;
}>) => {
  const {
    resolvedCountryCode
  } = useCountryCode();
  const router = useRouter();
  const {
    user
  } = useUser();
  const ready = useIsLoggerUserInfoSet();
  const [impressionId] = useState(createTrackingId);
  const [visible, setVisible] = useState(false);
  const tracked = useRef(false);
  const ref = useImpressionRef<HTMLDivElement>(() => setVisible(true));
  const countryCode = getSchoolCountryCode(resolvedCountryCode, user?.countryCode);
  const context = useMemo<SchoolCtaEvent>(() => ({
    ...getSchoolFunnelContext({
      countryCode,
      available,
      source: 'pricing'
    }),
    cta_type: ctaType,
    cta_location: ctaLocation ?? (countryCode === 'KR' ? 'pricing_school_tab_kr' : 'pricing_school_tab_global'),
    impression_id: impressionId
  }), [countryCode, available, ctaType, ctaLocation, impressionId]);
  useEffect(() => {
    if (!ready || !visible || tracked.current || ctaType === 'purchase' && !available) return;
    tracked.current = true;
    trackEvent('school_package_cta_viewed', context);
  }, [ready, visible, ctaType, available, context]);
  return <div className={className} ref={ref} onClick={() => {
    if (ctaType === 'purchase' && !available) return;
    const clickContext = ctaType === 'purchase' ? {
      ...context,
      checkout_id: createTrackingId()
    } : context;
    if (ctaType === 'purchase') {
      // Legacy planType means the clicked plan; preserve the existing series.
      trackEvent('click_pricing_card_cta', {
        ...clickContext,
        // eslint-disable-next-line zep/no-restricted-syntax -- Existing clicked-plan contract, not current-user context.
        planType,
        from: router.pathname
      });
    }
    onClick(clickContext);
  }} data-sentry-component="SchoolPackageCta" data-sentry-source-file="SchoolPackageCta.tsx">
      {children}
    </div>;
};